import { TranslationKey } from '../../../../types/translation/TranslationKey';
import React, { useContext, useState } from 'react';
import LoadingPackage from '../../Common/LoadingPackage';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid } from '@mui/material';
import { t, translate } from '../../../../types/translation/Translator';
import { ProductContext } from '../../../../context/ProductContext';
import { LotContext } from '../../../../context/LotContext';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { removeDiacritics, toFilterString } from '../../../../util/string.util';
import { Product } from '../../../../types/product';
import FlexPane from '../../../../VentoryUI/components/common/FlexPane';
import Pane from '../../../../VentoryUI/components/common/Pane';
import Paper from '../../../../VentoryUI/components/common/Paper';
import Table from '../../../components/Table';
import { BinContext } from '../../../../context/BinContext';
import dayjs from 'dayjs';
import DeleteLotModal from './DeleteLotModal';
import { Routing } from '../../../../util/routes.util';
import { BackButton, DeleteButton } from '../../../components/Button';
import GenerateBarcodeButton from '../../../components/Button/templates/GenerateBarcodeButton';
import { BarcodeExportEntityType } from '../../../../types/barcode_export_configuration/barcodeExportConfiguration';
import { Searchbar } from '../../../components/TextInput';
import { UnitOfMeasure } from '../../../../types/uom';
import { UserContext } from '../../../../context/UserContext';

export default function LotDetailPane() {
  const id = useParams()['id'] || '';

  const navigate = useNavigate();

  if (!id) return null; // TODO: Entity not found

  const { lots, lotsLoading } = useContext(LotContext);
  const { bins } = useContext(BinContext);
  const { products } = useContext(ProductContext);
  const { locale } = useContext(UserContext);

  const { productMasterData } = useContext(ProductMasterDataContext);

  const [filter, setFilter] = useState<string>('');
  const [openDelete, setOpenDelete] = useState(false);

  const lot = lots.get(id);
  if (!lot) return null; // TODO: Entity not found

  const items = [...products.values()]
    .map(v => [...v.values()])
    .flat()
    .filter(item => item.lotId === lot?.id);

  const hasLPN = !!items.filter(item => item.lpn).length;
  const hasSerial = !!items.filter(item => item.serial).length;

  const handleFilter = (item: Product) => {
    if (
      removeDiacritics(toFilterString(productMasterData.get(item.productMasterDataId)?.productName)).includes(filter) ||
      removeDiacritics(toFilterString(productMasterData.get(item.productMasterDataId)?.productNumber)).includes(
        filter,
      ) ||
      removeDiacritics(toFilterString(bins.get(item.binId)?.name)).includes(filter) ||
      removeDiacritics(toFilterString(lots.get(item.lotId || '')?.number)).includes(filter) ||
      removeDiacritics(toFilterString(item.serial)).includes(filter) ||
      removeDiacritics(toFilterString(item.lpn)).includes(filter)
    ) {
      return true;
    }
    return false;
  };

  const headers = [
    {
      key: 'productName',
      name: t(TranslationKey.productName).singular.label,
      text: (item: Product) => productMasterData.get(item.productMasterDataId)?.productName || 'Unknown Product Name',
    },
    {
      key: 'productNumber',
      name: t(TranslationKey.productNumber).singular.label,
      text: (item: Product) =>
        productMasterData.get(item.productMasterDataId)?.productNumber || 'Unknown Product Number',
    },
    {
      key: 'lpn',
      name: t(TranslationKey.lpn).singular.label,
      text: (item: Product) => item.lpn || '',
    },
    {
      key: 'serialNumber',
      name: t(TranslationKey.serial).singular.label,
      text: (item: Product) => item.serial || '',
    },
    {
      key: 'quantity',
      sortValue: (item: Product) => item.unitQuantity,
      name: t(TranslationKey.quantity).singular.label,
      text: (item: Product) =>
        UnitOfMeasure.toQuantityWithSuffix(
          productMasterData.get(item.productMasterDataId)?.unitOfMeasure,
          item.unitQuantity,
        ),
    },
  ].filter(item => {
    if (item.key === 'serialNumber' && !hasSerial) return false;
    if (item.key === 'lpn' && !hasLPN) return false;

    return true;
  });

  return (
    <>
      <DeleteLotModal open={openDelete} setOpen={setOpenDelete} idsToDelete={new Set([lot!.id])} />
      <Pane>
        {lotsLoading && !lots.get(id) ? (
          <Grid container alignItems={'center'} className='h-full'>
            <Grid item className='fill-gray-300 ' marginX={'auto'}>
              <LoadingPackage />
            </Grid>
          </Grid>
        ) : (
          <FlexPane
            header={
              <Paper>
                <Grid container rowSpacing={2} columnSpacing={2}>
                  <Grid item xs={6}>
                    <Grid container columnSpacing={1}>
                      <Grid item flexGrow={1} marginY={'auto'}>
                        <p className='font-semibold'>{t(TranslationKey.lot).singular.label}</p>
                        <p className='mt-2 text-xl font-bold text-gray-400'>{lot?.number}</p>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={6} marginY={'auto'}>
                    <p className='font-semibold'>{t(TranslationKey.expirationDate).singular.label}</p>
                    <p className='mt-2 text-xl font-bold text-gray-400'>
                      {lot?.expirationDate ? dayjs(lot?.expirationDate).toDate().toLocaleDateString(locale) : ''}
                    </p>
                  </Grid>
                </Grid>
              </Paper>
            }
            content={
              <FlexPane
                header={
                  <div className='flex justify-between gap-4'>
                    <Searchbar
                      value={filter}
                      onChange={value => setFilter(value)}
                      placeholder={t(TranslationKey.filterProducts).singular.label}
                    />
                    <div className='flex gap-2'>
                      <GenerateBarcodeButton
                        text={translate({ key: TranslationKey.generateBarcode, count: 'plural' })}
                        entityType={BarcodeExportEntityType.product}
                        entityIds={items.map(i => i.id)}
                      />
                    </div>
                  </div>
                }
                content={<Table loading={lotsLoading} items={items.filter(handleFilter)} headers={headers} />}
              />
            }
            footer={
              <div className='flex justify-between'>
                <div className='flex gap-2'>
                  <DeleteButton onClick={() => setOpenDelete(true)} />
                </div>
                <div className='flex gap-2'>
                  <BackButton onClick={() => navigate(Routing.stock.lots.overview())} />
                  <GenerateBarcodeButton
                    text={'Generate lot barcode'}
                    entityType={BarcodeExportEntityType.lot}
                    entityIds={[id]}
                  />
                </div>
              </div>
            }
          />
        )}
      </Pane>
    </>
  );
}
